import { useEffect, useMemo, useState } from "react";
import { http, fmtINR, fmtNum } from "@/lib/api";

const VERDICT_STYLES = {
    Correct: "bg-emerald-500/10 text-emerald-600 border-emerald-500/30",
    Under: "bg-blue-500/10 text-blue-600 border-blue-500/30",
    Excess: "bg-red-500/10 text-red-600 border-red-500/30",
    NoEligibleOffer: "bg-amber-500/10 text-amber-600 border-amber-500/30",
};

export default function DiscountCompliance() {
    const [data, setData] = useState(null);
    const [offers, setOffers] = useState([]);
    const [verdict, setVerdict] = useState("All");

    useEffect(() => {
        http.get("/spykar/discount-compliance", { params: { limit: 500 } }).then((r) => setData(r.data));
        http.get("/spykar/offers").then((r) => setOffers(r.data || []));
    }, []);

    const t = data?.totals;
    const filtered = useMemo(() => {
        const rows = data?.rows || [];
        return verdict === "All" ? rows : rows.filter((r) => r.verdict === verdict);
    }, [data, verdict]);

    return (
        <div className="mx-auto max-w-[1560px] space-y-4 p-6" data-testid="discount-compliance-page">
            <div className="flex items-end justify-between">
                <div>
                    <div className="mlabel">Spykar Ops · Discount Compliance</div>
                    <h1 className="text-[22px] font-semibold tracking-tight">Post-Sale Discount Audit</h1>
                    <p className="mt-1 text-[12px] text-muted-foreground">
                        Evaluates each EOSS row against configured offer catalog. Flags excess &amp; under discounts.
                    </p>
                </div>
                <div className="text-[11px] text-muted-foreground">
                    {t ? `${fmtNum(t.evaluated_rows)} rows evaluated` : "…"}
                </div>
            </div>

            {t && (
                <div className="grid grid-cols-2 gap-3 md:grid-cols-6">
                    <Stat label="Correct" value={fmtNum(t.Correct || 0)} tone="green" />
                    <Stat label="Excess Discount" value={fmtNum(t.Excess || 0)} tone="red" />
                    <Stat label="Under Discount" value={fmtNum(t.Under || 0)} tone="blue" />
                    <Stat label="No Eligible Offer" value={fmtNum(t.NoEligibleOffer || 0)} tone="amber" />
                    <Stat label="Leakage (Excess ₹)" value={fmtINR(t.leakage_value)} tone="red" />
                    <Stat label="Under-Disc (₹)" value={fmtINR(t.under_discount_value)} tone="blue" />
                </div>
            )}

            {/* Offer catalog */}
            <div className="rounded-sm border border-border bg-card p-4">
                <div className="mlabel mb-2">Active Offer Catalog</div>
                <div className="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3">
                    {offers.map((o) => (
                        <div key={o.id} className="rounded-sm border border-border bg-muted/30 p-2.5">
                            <div className="flex items-center justify-between">
                                <div className="text-[12px] font-semibold">{o.name}</div>
                                <span
                                    className={
                                        "rounded-sm border px-1.5 py-[1px] text-[10px] font-medium " +
                                        (o.active
                                            ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/30"
                                            : "bg-muted text-muted-foreground border-border")
                                    }
                                >
                                    {o.active ? "Active" : "Inactive"}
                                </span>
                            </div>
                            <div className="mt-1 text-[11px] text-muted-foreground">
                                {o.construct}
                                {o.percent != null && ` · ${o.percent}%`}
                                {o.flat != null && ` · ₹${o.flat}`}
                                {o.min_qty != null && ` · min ${o.min_qty}`}
                                {o.categories?.length ? ` · ${o.categories.join(", ")}` : ""}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Filter tabs */}
            <div className="flex w-fit items-center gap-1 rounded-sm border border-border bg-card p-0.5">
                {["All", "Excess", "Under", "NoEligibleOffer", "Correct"].map((v) => (
                    <button
                        key={v}
                        onClick={() => setVerdict(v)}
                        data-testid={`compliance-tab-${v}`}
                        className={
                            "rounded-sm px-2.5 py-1 text-[11px] font-medium transition-colors " +
                            (verdict === v
                                ? "bg-primary text-primary-foreground"
                                : "text-muted-foreground hover:text-foreground")
                        }
                    >
                        {v}
                    </button>
                ))}
            </div>

            <div className="overflow-hidden rounded-sm border border-border bg-card" data-testid="compliance-table">
                <div className="max-h-[calc(100vh-380px)] overflow-auto">
                    <table className="w-full text-[12px]">
                        <thead className="sticky top-0 z-10 bg-muted/60 text-[10px] uppercase tracking-wider text-muted-foreground">
                            <tr>
                                <th className="px-3 py-2 text-left">Date</th>
                                <th className="px-3 py-2 text-left">State</th>
                                <th className="px-3 py-2 text-left">Retailer</th>
                                <th className="px-3 py-2 text-left">Invoice</th>
                                <th className="px-3 py-2 text-left">EAN</th>
                                <th className="px-3 py-2 text-left">Cat</th>
                                <th className="px-3 py-2 text-right">Qty</th>
                                <th className="px-3 py-2 text-right">MRP</th>
                                <th className="px-3 py-2 text-right">Actual Disc</th>
                                <th className="px-3 py-2 text-right">Expected Disc</th>
                                <th className="px-3 py-2 text-left">Offer</th>
                                <th className="px-3 py-2 text-left">Verdict</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((r) => (
                                <tr key={r.id} className="border-t border-border hover:bg-muted/40">
                                    <td className="mono-num px-3 py-1.5 text-muted-foreground">{r.invoice_date}</td>
                                    <td className="px-3 py-1.5">{r.state}</td>
                                    <td className="px-3 py-1.5">{r.retailer}</td>
                                    <td className="mono-num px-3 py-1.5">{r.invoice_no}</td>
                                    <td className="mono-num px-3 py-1.5 text-muted-foreground">{r.ean}</td>
                                    <td className="px-3 py-1.5">{r.category}</td>
                                    <td className="mono-num px-3 py-1.5 text-right">{fmtNum(r.qty)}</td>
                                    <td className="mono-num px-3 py-1.5 text-right">{fmtINR(r.mrp_val)}</td>
                                    <td className="mono-num px-3 py-1.5 text-right text-amber-600">{fmtINR(r.actual_discount)}</td>
                                    <td className="mono-num px-3 py-1.5 text-right">{fmtINR(r.expected_discount)}</td>
                                    <td className="px-3 py-1.5 text-[11px] text-muted-foreground">{r.offer_applied || "—"}</td>
                                    <td className="px-3 py-1.5">
                                        <span
                                            className={
                                                "rounded-sm border px-1.5 py-[1px] text-[10px] font-medium " +
                                                (VERDICT_STYLES[r.verdict] || "bg-muted text-muted-foreground border-border")
                                            }
                                        >
                                            {r.verdict}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                            {filtered.length === 0 && (
                                <tr>
                                    <td colSpan={12} className="px-3 py-10 text-center text-[12px] text-muted-foreground">
                                        No EOSS rows loaded. Upload an EOSS Credit-Note working file to run compliance.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

function Stat({ label, value, tone }) {
    const c =
        tone === "green"
            ? "text-emerald-600"
            : tone === "amber"
            ? "text-amber-600"
            : tone === "red"
            ? "text-red-500"
            : tone === "blue"
            ? "text-blue-600"
            : "";
    return (
        <div className="rounded-sm border border-border bg-card p-3">
            <div className="mlabel">{label}</div>
            <div className={`mono-num mt-1 text-xl font-semibold ${c}`}>{value}</div>
        </div>
    );
}
